import React from 'react';
import { Typography, Box, styled } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { Button } from 'components/Button/Button';
import { useThemeUtils } from 'contexts/ThemeContext/hooks/useThemeUtils';
import { UnexpectedErrorThreeSVG as UnexpectedErrorThreeComponent } from 'components/ErrorStateSVGComp';

interface ErrorStateProps {
  onRetryClick?: () => void;
}

const Container = styled(Box)(({ theme }) => ({
  height: '100vh',
  width: '100vw',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  gap: theme.spacing(0.5),
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  fontWeight: 400,
  marginTop: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.palette.grey[400],
  },
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '250px',
  '& > svg': {
    width: '100%',
    height: 'auto',
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '180px',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    maxWidth: '235px',
  },
}));

export const AssessmentsErrorState = ({ onRetryClick }: ErrorStateProps) => {
  const { theme } = useThemeUtils();
  const errorTitleId = React.useId();
  const errorSubtitleId = React.useId();

  return (
    <Container aria-labelledby={errorTitleId} aria-describedby={errorSubtitleId}>
      <ImageContainer>
        <UnexpectedErrorThreeComponent />
      </ImageContainer>
      <Typography id={errorTitleId} variant="h6" color="primary">
        Something went wrong
      </Typography>
      <Typography id={errorSubtitleId} variant="body1" color="grey.800">
        Please try again later.
      </Typography>
      {onRetryClick && (
        <StyledButton variant="tertiary" onClick={onRetryClick} aria-label="Retry">
          Retry
          <ReplayIcon
            fontSize="small"
            sx={{
              color: theme.palette.brandShade1.main,
              transform: 'scaleX(-1)',
            }}
            aria-hidden="true"
          />
        </StyledButton>
      )}
    </Container>
  );
};
